/**
 * 🍦 MENU PAGE - Gemini Design + Gamification
 * Beautiful product cards with rewards on add!
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShoppingCart } from 'lucide-react';
import { useCartStore, API_BASE_URL } from '../store';
import { ProductDetail } from '../components/ProductDetail';
import { AddReward, Toast } from '../components/AddReward';
import styles from './Menu.module.css';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price_cents: number;
  category: string;
  image_url?: string;
}

const categories = [
  { id: 'soft_serve', label: 'Soft Serve', icon: '🍦' },
  { id: 'sundaes', label: 'Sundaes', icon: '🍨' },
  { id: 'drinks', label: 'Drinks', icon: '🥤' },
  { id: 'toppings', label: 'Toppings', icon: '🍫' },
];

// Map item IDs to their images
const itemImages: Record<string, string> = {
  'FM002': '/softserve-swirl.png', // Tropical Twist
  'FM003': '/softserve-transchocdip.png', // Choc Dip Cone
  'FM004': '/softserve-tranvanillalicious.png', // Vanillalicious
};

const getItemImage = (itemId: string) => {
  return itemImages[itemId] || '/softserve-tran.png';
};

const mockMenuItems: MenuItem[] = [
  { id: 'FM001', name: 'Classic Mango', description: 'Fresh Queensland mango swirled into our signature soft serve. A Townsville favorite since 1989!', price_cents: 650, category: 'soft_serve' },
  { id: 'FM002', name: 'Tropical Twist', description: 'Mango meets Vanillalicious in this tropical explosion', price_cents: 700, category: 'soft_serve' },
  { id: 'FM003', name: 'Choc Dip Cone', description: 'Classic mango dipped in rich chocolate shell', price_cents: 550, category: 'soft_serve' },
  { id: 'FM004', name: 'Vanillalicious', description: 'Pure, creamy vanilla soft serve', price_cents: 500, category: 'soft_serve' },
  { id: 'FM009', name: 'Coming soon!', description: 'Classic banana split with three flavors and all the toppings', price_cents: 950, category: 'sundaes' },
  { id: 'FM010', name: 'Mango Smoothie', description: 'Fresh mango blended with ice and lime', price_cents: 600, category: 'drinks' },
  { id: 'FM011', name: 'Thickshake - Chocolate', description: 'Rich and creamy chocolate thickshake', price_cents: 650, category: 'drinks' },
];

export function MenuPage() {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState<MenuItem[]>(mockMenuItems);
  const [selectedCategory, setSelectedCategory] = useState('soft_serve');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [showPopular, setShowPopular] = useState(true);
  
  // Gamification state
  const [showReward, setShowReward] = useState(false);
  const [rewardPosition, setRewardPosition] = useState({ x: 0, y: 0 });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [cartBounce, setCartBounce] = useState(false);
  
  const cartTotal = useCartStore((state) => state.totalItems());
  const addItem = useCartStore((state) => state.addItem);

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu`);
      const data = await response.json();
      if (data.items?.length) {
        setMenuItems(data.items);
      }
    } catch (error) {
      console.log('Using mock menu data');
    }
  };

  const filteredItems = menuItems.filter(
    (item) =>
      item.category === selectedCategory &&
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const popularItems = menuItems.filter(item =>
    ['FM001', 'FM004'].includes(item.id)
  );

  const funMessages = [
    'Yum! Great choice! 🍦',
    'Delicious pick! 🥭',
    'Treat yourself! ✨',
    'Nice one! 🧡',
    'Mango magic! 🍦',
    'Sweet! 🎉',
  ];

  const handleQuickAdd = useCallback((item: MenuItem, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Get click position for animation
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setRewardPosition({ 
      x: rect.left + rect.width / 2, 
      y: rect.top 
    });
    
    // Add to cart
    addItem({
      menuItemId: item.id,
      name: item.name,
      price: item.price_cents / 100,
      quantity: 1,
      imageUrl: item.image_url,
    });
    
    // Trigger reward animation
    setShowReward(true);
    
    // Bounce cart icon
    setCartBounce(true);
    setTimeout(() => setCartBounce(false), 600);
    
    // Show toast
    const randomMessage = funMessages[Math.floor(Math.random() * funMessages.length)];
    setToastMessage(randomMessage);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, [addItem]);

  const handleRewardComplete = useCallback(() => {
    setShowReward(false);
  }, []);

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.logoContainer}>
          <img src="/fmlogo.png" alt="Frosty Mango" className={styles.headerLogo} />
        </div>
        <button 
          className={`${styles.cartButton} ${cartBounce ? styles.cartBounce : ''}`}
          onClick={() => navigate('/cart')}
        >
          <ShoppingCart size={24} />
          {cartTotal > 0 && (
            <span className={`${styles.cartBadge} ${cartBounce ? styles.badgePop : ''}`}>
              {cartTotal}
            </span>
          )}
        </button>
      </header>

      {/* Search */}
      <div className={styles.searchContainer}>
        <Search size={20} className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search menu..."
          className={styles.searchInput}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Categories */}
      <div className={styles.categories}>
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`${styles.categoryPill} ${
              selectedCategory === cat.id ? styles.categoryPillActive : ''
            }`}
            onClick={() => {
              setSelectedCategory(cat.id);
              setShowPopular(false);
            }}
          >
            <span>{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Popular Section - hides after interaction */}
      {showPopular && (
        <div className={styles.popularSection}>
          <h2 className={styles.sectionTitle}>POPULAR RIGHT NOW 🔥</h2>
          <div className={styles.popularGrid}>
            {popularItems.map((item) => (
              <div 
                key={item.id} 
                className={styles.popularCard}
                onClick={() => {
                  setSelectedItem(item);
                  setShowPopular(false);
                }}
              >
                <div className={styles.popularImage}>
                  <img 
                    src={getItemImage(item.id)} 
                    alt={item.name}
                    className={styles.popularImg}
                  />
                </div>
                <div className={styles.popularInfo}>
                  <h3 className={styles.popularName}>{item.name}</h3>
                  <p className={styles.popularDesc}>
                    {item.description.slice(0, 30)}...
                  </p>
                  <div className={styles.popularFooter}>
                    <span className={styles.popularPrice}>
                      ${(item.price_cents / 100).toFixed(2)}
                    </span>
                    <button 
                      className={styles.addButtonSmall}
                      onClick={(e) => handleQuickAdd(item, e)}
                    >
                      [ADD]
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Menu Grid */}
      <div className={styles.menuSection}>
        <h2 className={styles.sectionTitle}>
          {categories.find(c => c.id === selectedCategory)?.label}
        </h2>
        <div className={styles.menuGrid}>
          {filteredItems.map((item) => (
            <div 
              key={item.id} 
              className={styles.menuCard}
              onClick={() => {
                setSelectedItem(item);
                setShowPopular(false);
              }}
            >
              <div className={styles.menuImage}>
                <img 
                  src={getItemImage(item.id)} 
                  alt={item.name}
                  className={styles.menuImg}
                />
              </div>
              <div className={styles.menuInfo}>
                <h3 className={styles.menuName}>{item.name}</h3>
                <p className={styles.menuDesc}>{item.description.slice(0, 40)}...</p>
                <div className={styles.menuFooter}>
                  <span className={styles.menuPrice}>
                    ${(item.price_cents / 100).toFixed(2)}
                  </span>
                  <button 
                    className={styles.addButton}
                    onClick={(e) => handleQuickAdd(item, e)}
                  >
                    [ADD]
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedItem && (
        <ProductDetail
          item={selectedItem}
          items={filteredItems}
          onClose={() => setSelectedItem(null)}
          onNavigate={(item) => setSelectedItem(item)}
        />
      )}

      {/* Reward Animation */}
      <AddReward 
        show={showReward}
        startPosition={rewardPosition}
        onComplete={handleRewardComplete}
      />

      {/* Toast */}
      <Toast message={toastMessage} show={showToast} />
    </div>
  );
}
